import { nextTick, onBeforeUnmount, onBeforeUpdate, onMounted, onUpdated, watch, type Ref } from 'vue'

type ScrollAction = 'restoreScrollBottom' | 'restoreScrollTop' | 'scrollToBottom' | 'scrollToTop'

interface ScrollRestorationOptions<T> {
  listTail: Ref<T>
  listHead: Ref<T>
  onTailChange?: (tail: T, scrollInfo: { scrollTop: number; scrollBottom: number }) => ScrollAction | undefined
  onHeadChange?: (head: T, scrollInfo: { scrollTop: number; scrollBottom: number }) => ScrollAction | undefined
}

export function useScrollRestoration<T>(
  targetEl: Ref<HTMLElement | null>,
  { listTail, listHead, onTailChange, onHeadChange }: ScrollRestorationOptions<T>
) {
  let scrollTop = 0
  let scrollBottom = 0
  let isScrollPositionUpdatesEnabled = false
  let canRestoreScrollTop = false

  const updateScrollPosition = () => {
    const targetElVal = targetEl.value
    if (!targetElVal) return

    scrollTop = targetElVal.scrollTop
    scrollBottom = targetElVal.scrollHeight - targetElVal.clientHeight - targetElVal.scrollTop
  }

  const onScroll = () => {
    if (!isScrollPositionUpdatesEnabled) return
    updateScrollPosition()
  }

  const restoreScrollTop = () => {
    const targetElVal = targetEl.value
    if (!targetElVal) return

    targetElVal.scrollTo({ top: scrollTop, behavior: 'instant' })
  }

  const scrollToTop = () => {
    const targetElVal = targetEl.value
    if (!targetElVal) return
    targetElVal.scrollTo({
      top: 0,
      behavior: 'smooth',
    })
  }

  const restoreScrollBottom = () => {
    const targetElVal = targetEl.value
    if (!targetElVal) return

    const targetScrollTop = targetElVal.scrollHeight - targetElVal.clientHeight - scrollBottom
    targetElVal.scrollTo({ top: targetScrollTop, behavior: 'instant' })
  }

  const scrollToBottom = () => {
    const targetElVal = targetEl.value
    if (!targetElVal) return
    targetElVal.scrollTo({ top: targetElVal.scrollHeight + targetElVal.clientHeight, behavior: 'smooth' })
  }

  const abortController = new AbortController()

  const initTargetEl = () => {
    const targetElVal = targetEl.value
    if (!targetElVal) return

    targetElVal.addEventListener('scroll', onScroll, { signal: abortController.signal })
    targetElVal.scrollTo(0, targetElVal.scrollHeight)

    window.addEventListener('focus', updateScrollPosition, { signal: abortController.signal })

    setTimeout(() => {
      canRestoreScrollTop = true
      isScrollPositionUpdatesEnabled = true
    })
  }

  onMounted(() => {
    nextTick(() => {
      initTargetEl()
    })
  })

  onBeforeUpdate(() => {
    isScrollPositionUpdatesEnabled = false
  })

  onUpdated(() => {
    isScrollPositionUpdatesEnabled = true

    setTimeout(() => {
      if (isScrollPositionUpdatesEnabled) updateScrollPosition()
    })
  })

  const actionHandlers = {
    restoreScrollTop,
    scrollToTop,
    restoreScrollBottom,
    scrollToBottom,
  }

  watch(listHead, () => {
    const action = onHeadChange?.(listHead.value, { scrollTop, scrollBottom })

    if (action) return actionHandlers[action]()

    if (!canRestoreScrollTop) return
    nextTick(restoreScrollTop)
  })

  watch(listTail, () => {
    const action = onTailChange?.(listTail.value, { scrollTop, scrollBottom })

    if (action) return actionHandlers[action]()

    nextTick(restoreScrollBottom)
  })

  onBeforeUnmount(() => {
    abortController.abort()
    canRestoreScrollTop = false
  })
}
